import {IArtistItem, StateInterface} from '../interfaces/interfaces';
import { applyJPlist } from '../artists-jplist/artists-jplist';
import { generateDeepLink, updateDeepLink, getUrlParams } from './artists-deep-link-provider';
import { getFromStorage } from './artists-page-storage-provider';
import { getJSONFromHtml } from '../../../../domain/common';
import { DESKTOP_BREAKPOINT, MOBILE_BREAKPOINT } from '../../../../domain/animations/animations-provider';

// if window width is <= LIST_TO_GRID_ENDPOINT => list view automatically will be converted to the grid view
export const LIST_TO_GRID_ENDPOINT = 800;

// items per page
export const ITEMS_PER_PAGE_1 = 18;
export const ITEMS_PER_PAGE_2 = 42;
export const ITEMS_PER_PAGE_3 = 60;
export const ITEMS_PER_PAGE_ALL = 10000;

// ------------------------- DATA -----------------------------

/**
 * normalize product
 */
export const normalizeArtist = (artist: IArtistItem) => {

    if(!artist) return artist;
    // properties with inner HTML - normalizations
    // computed properties
    artist.isSeeMore = artist.link !== '';

    return artist;
};

/**
 * products data is located in HTML in DIV with id 'list-data'
 */
export const getArtistsData = () : IArtistItem[] => {

    const data = getJSONFromHtml(document.getElementById('artists-data')) as IArtistItem[]
    if(!data || !Array.isArray(data)) return [];

    for(let i=0; i<data.length; i++){
        data[i] = normalizeArtist(data[i]);
    }

    return data;
};

// ------------------------- MAIN -----------------------------

/**
 * refresh jPList according to the current state
 */
export const refresh = (state: StateInterface, shouldUpdateDeepLink: boolean = true) => {

    const res = applyJPlist(
        state.artistsInitial,
        state.category,
        // filter
        state.filterTerm,
        state.filterProperty,

        // pagination
        state.currentPage,
        state.itemsPerPage,
        state.paginationRange
    );

    if(shouldUpdateDeepLink) {

        // handle deep link: update deep link on each jPList change
        const deepLink = generateDeepLink(
            state.currentPage,
            state.itemsPerPage,
            state.layoutMode,
            state.filterTerm);

        updateDeepLink(deepLink);
    }

    return res;
};

/**
 * init deep link on page load;
 * parse the existing deep link, and add it to the current state
 */
export const deepLinkAndStorageInit = (state: StateInterface) => {

    // deep link
    const paramsMap = getUrlParams(window.location.hash);
    const props = ['currentPage', 'itemsPerPage', 'layoutMode', 'filterTerm'];

    // storage data
    const data = getFromStorage() as unknown as StateInterface;

    for(let i=0; i<props.length; i++){
        const prop = props[i];

        if(paramsMap.has(prop)){
            state[prop] = paramsMap.get(prop);
        }
        else{
            if(data && data[prop]){
                state[prop] = data[prop];
            }
        }
    }
};

// ------------------------- UI -----------------------------

/**
 * set equal height for every element in the group the highest one
 */
const setGroupHeight = (group: HTMLElement[][]) => {
    if(!group || group.length <= 1) return;

    let namesMaxHeight = 0;
    let titlesMaxHeight = 0;
    let creditsMaxHeight = 0;
    let quotesMaxHeight = 0;

    group.forEach(item => {
        const $name = item[0];
        const $title = item[1];
        const $credit = item[2];
        const $quote = item[3];

        namesMaxHeight = $name ? Math.max(namesMaxHeight, $name.offsetHeight) : 0;
        titlesMaxHeight = $title ? Math.max(titlesMaxHeight, $title.offsetHeight) : 0;
        creditsMaxHeight = $credit ? Math.max(creditsMaxHeight, $credit.offsetHeight) : 0;
        quotesMaxHeight = $quote ? Math.max(quotesMaxHeight, $quote.offsetHeight) : 0;
    });

    group.forEach(item => {
        const $name = item[0];
        const $title = item[1];
        const $credit = item[2];
        const $quote = item[3];

        if($name && namesMaxHeight > 0)  $name.style.height = `${ namesMaxHeight }px`;
        if($title && titlesMaxHeight > 0) $title.style.height = `${ titlesMaxHeight }px`;
        if($credit && creditsMaxHeight > 0) $credit.style.height = `${ creditsMaxHeight }px`;
        if($quote && quotesMaxHeight > 0) $quote.style.height = `${ quotesMaxHeight }px`;
    });
};

/**
 * titles and taglines should have equal height in grid view
 */
export const initEqualHeight = (
    $container: HTMLDivElement|null,
    width: number,
    layoutMode: string,
    desktopSize: number = 3,
    tabletSize: number = 2,
    mobileSize: number = 2) : Promise<void> => {

    return new Promise((resolve, reject) => {

        if(!$container){
            resolve();
            return;
        }

        window.setTimeout(() => {

            // remove height property from all current (for mobile, list layout, etc.)
            const $names = $container.querySelectorAll('[data-grid-artist-name="true"]') as NodeListOf<HTMLElement>;
            $names.forEach($name => {
                $name.style.height = '';
            });

            const $titles = $container.querySelectorAll('[data-grid-artist-title="true"]') as NodeListOf<HTMLElement>;
            $titles.forEach($title => {
                $title.style.height = '';
            });

            const $credits = $container.querySelectorAll('[data-grid-artist-credit="true"]') as NodeListOf<HTMLElement>;
            $credits.forEach($credit => {
                $credit.style.height = '';
            });

            const $quotes = $container.querySelectorAll('[data-grid-artist-quote="true"]') as NodeListOf<HTMLElement>;
            $quotes.forEach($quote => {
                $quote.style.height = '';
            });

            // in list view and in mobile -> no need to define the special heights
            if(
                // in case we have a mobile viewport, but only one grid item in the row (like in all list pages) ---> stop
                (mobileSize <= MOBILE_BREAKPOINT && mobileSize === 1) ||
                (layoutMode !== 'grid')){
                resolve();
                return;
            }

            // on list page, for example: 3 items per row in desktop, 2 items per row in tablet
            let groupSize = desktopSize;
            if(width <= MOBILE_BREAKPOINT){
                groupSize = mobileSize;
            }
            else if(width < DESKTOP_BREAKPOINT){
                groupSize = tabletSize;
            }

            // there is the same number of titles & taglines (equals to the # of products)
            const n = $titles.length;

            let group: HTMLElement[][] = [];
            let i=0;

            while(i < n){

                if(i % groupSize === 0){
                    setGroupHeight(group);
                    // new group start
                    group = [];
                }

                group.push([$names[i], $titles[i], $credits[i], $quotes[i]]);
                i++;
            }

            setGroupHeight(group);

            resolve();
        }, 0);
    });
};

// ------------------------- HELPERS -----------------------------


/**
 * get filter placeholder text from the hidden DIV
 * with data-attribute [data-jplist-search-title]
 * the DIV is placed in the top code mirror on the page as a plain HTML
 */
export const getFilterPlaceholderText = () => {

    let filterPlaceholderText = 'Search Artists';
    const $filterTextEl = document.querySelector('[data-jplist-search-title]');

    if($filterTextEl){
        filterPlaceholderText = $filterTextEl.getAttribute('data-jplist-search-title') ?? 'Search Artists';
    }

    return filterPlaceholderText;
};



